// Archivo: CotizCreatedIng.ts
// Ruta: src\static\js\Operaciones\Ingenieria\Cotiz\CotizCreatedIng.js
// Lenguaje: JavaScript

namespace CotizCreatedIng {

	declare class AutoNumeric {
		constructor(element: any, options?: any);
		set(value: any): void;
		getFormatted(): string;
		getNumericString(): string;
	}

	declare class Swal {
		constructor(element: any, options?: any);
		static fire(options: any): Promise<any>;
	}

	/**
	 * Funcion forzar el redondeo a 2 decimales
	 * @param value 
	 * @returns 
	 */
	function round2(value: string | number): number {
		if (value === null || value === undefined) return 0;

		// Convertimos SIEMPRE a string sin pasar por flotante de JS.
		let v = (typeof value === "number" ? String(value) : value).trim();

		// Limpiar comas
		v = v.replace(/,/g, "");

		if (v === "") return 0;

		// Redondeo EXACTO tipo Excel
		return new Decimal(v)
			.toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
			.toNumber();
	}


	class PartDTO {
		origin: string;
		partNum: string;
		description: string;
		quantity: number;
		unit: string;
		unitPrice: number;
		amount: number;
		date: Date;
		uuid: string;

		constructor(data: any) {
			this.origin = data.origin || "";
			this.partNum = data.partNum || "";
			this.description = data.description || "";
			this.quantity = toDecimal(data.quantity);
			this.unit = data.unit || "";
			this.unitPrice = toDecimal(data.unitPrice);
			this.amount = toDecimal(this.quantity * this.unitPrice);
			this.date = data.date ? new Date(data.date) : new Date();
			if (!data.uuid) {
				this.uuid = this.UUID_allPlatforms();
			} else {
				this.uuid = data.uuid;
			}
		}

		static fromArray(arr: any[]) {
			return Array.isArray(arr) ? arr.map((o) => new ConceptDto(o)) : [];
		}

		toDict() {
			return {
				uuid: this.uuid,
				origin: this.origin,
				partNum: this.partNum.toUpperCase(),
				description: this.description.toUpperCase(),
				quantity: this.quantity.toString().toUpperCase(),
				unit: this.unit.toUpperCase(),
				unitPrice: this.unitPrice,
				amount: this.amount,
			};
		}


		UUID_allPlatforms() {
			return URL.createObjectURL(new Blob()).slice(-36);
		}
	}



	/* Catalog of concepts for costing details */
	class CatalogConcepts {
		items: ConceptDto[];
		constructor() {
			this.items = [];
		}
		getAll() {
			return this.items;
		}

		add(item: ConceptDto) {
			this.items.push(item);
		}

		addItems(items: ConceptDto[]) {
			this.items.push(...items);
		}

		isEmpty() {
			return this.items.length === 0;
		}

		update(item: ConceptDto) {
			const idx = this.items.findIndex((p) => p.uuid === item.uuid);
			if (idx >= 0) this.items[idx] = item;
			else this.items.push(item);
		}

		remove(uuid: string) {
			this.items = this.items.filter((p) => p.uuid !== uuid);
		}

		clear() {
			this.items = [];
		}

		to_Json_Q_CostingDetail(costingID, opts = {}) {
			const { startIndex = 1 } = opts;
			const r2 = (n) => Number(Number(n).toFixed(2));

			return this.items.map((item, idx) => ({
				CostingID: costingID,
				CostingLine: startIndex + idx,
				PartNum: item.partNum,
				PartDescription: item.description,
				Qty: Number(item.quantity),
				UOMCode: item.unit,
				UnitPrice: r2(item.unitPrice),
				Amount: r2(item.amount ?? (item.quantity * item.unitPrice)),
			}));
		}

		getTotalAmount() {
			return toDecimal(this.items.reduce((sum, item) => sum + item.amount, 0));
		}

	}

	class LocalDb {
	concepts = [];
	static _storage: Record<string, any[]> = {};

	key: string;

	constructor(key = "concepts") {
		this.key = key;
		this.concepts = [];

		// Garantizamos que siempre exista la "tabla" en memoria
		if (!LocalDb._storage[this.key]) {
			LocalDb._storage[this.key] = [];
		}
	}

	getAll(): ItemDTO[] {
		const raw = LocalDb._storage[this.key];
		if (!raw) return [];
		try {
			// Copia profunda para imitar localStorage
			return ItemDTO.fromArray(JSON.parse(JSON.stringify(raw)));
		} catch (e) {
			console.error("Error recuperando datos de memoria:", e);
			return [];
		}
	}

	private _save(arr: ItemDTO[]) {
		LocalDb._storage[this.key] = arr.map((p) => p.toDict());
	}

	add(item: ItemDTO) {
		const arr = this.getAll();
		arr.push(item);
		this._save(arr);
	}

	addItems(items: ItemDTO[]) {
		const arr = this.getAll();
		arr.push(...items);
		this._save(arr);
	}

	update(item: ItemDTO) {
		const arr = this.getAll();
		const idx = arr.findIndex((p) => p.uuid === item.uuid);
		if (idx >= 0) {
			arr[idx] = item;
		} else {
			console.error("No se encontró uuid, se agrega:", item.uuid);
			arr.push(item);
		}
		this._save(arr);
	}

	remove(uuid: string) {
		const arr = this.getAll().filter((p) => p.uuid !== uuid);
		this._save(arr);
	}

	clear() {
		LocalDb._storage[this.key] = [];
	}

	isEmpty(): boolean {
		return this.getAll().length === 0;
	}

	to_Json_Q_CostingDetail(costingID: number, opts: { startIndex?: number } = {}) {
		const { startIndex = 1 } = opts;
		const items = this.getAll();
		const r2 = (n: number) => Number(Number(n).toFixed(2));

		return items.map((item, idx) => ({
			CostingID: costingID,
			CostingLine: startIndex + idx,
			PartNum: item.partNum,
			PartDescription: item.description,
			Qty: Number(item.quantity),
			UOMCode: item.unit,
			UnitPrice: r2(item.unitPrice),
			Amount: r2(item.quantity * item.unitPrice),
		}));
	}
}

	class ItemDTO {
		constructor(data = {}) {
			this.origin = data.origin || "Elephant";
			this.partNum = data.partNum || "";
			this.description = data.description || "";
			this.quantity = Number(data.quantity ?? 1);
			this.unit = data.unit || "";
			this.unitPrice =
				data.unitPrice != null
					? Number(parseFloat(data.unitPrice).toFixed(2))
					: 0;
			this.amount =
				data.amount != null ? Number(parseFloat(data.amount).toFixed(2)) : 0;
			this.date = data.date ? new Date(data.date) : new Date();
			if (!data.uuid) {
				this.assignUUID();
			} else {
				this.uuid = data.uuid;
			}
		}

		static fromArray(arr) {
			return Array.isArray(arr) ? arr.map((o) => new ItemDTO(o)) : [];
		}

		toDict() {
			return {
				uuid: this.uuid,
				origin: this.origin,
				partNum: this.partNum.toUpperCase(),
				description: this.description.toUpperCase(),
				quantity: this.quantity.toString().toUpperCase(),
				unit: this.unit.toUpperCase(),
				unitPrice: this.unitPrice,
				amount: this.amount,
			};
		}

		assignUUID() {
			this.uuid = URL.createObjectURL(new Blob()).slice(-36);
		}
	}

	class GlobalVariables_Class {
		UserID = null; // ID del usuario actual

		FilesToUpload = []; // lista de objetos completos { id, file, addedAt }
		PlainFiles = [];// opcional: lista plana de File

		totalAmount = 0;

		setUser(id) {
			this.UserID = id;
		}

		// Reemplaza TODO con la lista completa que emite el componente
		setAllFiles(objectsArray = [], plainFiles = []) {
			this.FilesToUpload = Array.from(objectsArray);
			this.PlainFiles = Array.from(plainFiles);
		}

		clearFiles() {
			this.FilesToUpload = [];
			this.PlainFiles = [];
		}

		getFilesObjects() {
			return this.FilesToUpload;
		}

		getFiles() {
			return this.PlainFiles;
		}
	}

	const globalVariables = new GlobalVariables_Class();
	window.globalVariables = globalVariables; // si necesitas acceso global


	let downloadFormat = null;
	let fileInput = null;

	let activeCase: CostingForm | null = null;


	// Calculo financiero
	let isNeodata = null;
	let isTender = null;



	// Se usa cuando se recupera la task del enpoint
	let task_TaskID = null
	let task_SellerUserID = null
	let docsID = null


	const btn_agregar = document.getElementById("btn_agregar");
	let modalEdicionParte = null;
	let isModalInEdition = false;
	let uuidEditionElement = null;

	let localDatabase = new LocalDb();

	let itemList: ItemDTO[] = null;

	let validador = null

	// ===============================
	// 🔹 CONSTANTES Y VARIABLES
	// ===============================
	let table; // Variable para almacenar la instancia de DataTable
	let selectedDistributorId = null; // ID del distribuidor seleccionado
	let selectedDistributorIdForReject = null;

	// Exponer funciones usadas inline
	window.loadPartIntoModal = loadPartIntoModal;
	window.confirmDelete = confirmDelete;

	let parts = null;
	let concepts = [];




	const ExcelJson = (() => {
		// --- helpers ---
		const normVal = (v) => {
			if (v === null || v === undefined) return "";
			if (v instanceof Date) return v.toISOString();
			if (typeof v === "object" && v && "text" in v && typeof v.text === "string")
				return v.text;
			return String(v);
		};
		const isCellEmpty = (v) => normVal(v).trim() === "";
		const trimRightEmptyCells = (rowVals) => {
			if (!rowVals) return [];
			let end = rowVals.length - 1;
			while (end >= 0 && isCellEmpty(rowVals[end])) end--;
			return rowVals.slice(0, end + 1);
		};
		const parseDecimal = (raw) => {
			if (raw === null || raw === undefined) return null;
			let s = String(raw).trim();
			if (!s) return null;
			const lastComma = s.lastIndexOf(",");
			const lastDot = s.lastIndexOf(".");
			if (lastComma > lastDot) {
				s = s.replace(/\./g, ""); // quita miles
				s = s.replace(",", "."); // coma -> punto
			} else {
				s = s.replace(/,/g, ""); // quita miles
			}
			const n = Number(s);
			return Number.isFinite(n) ? n : null;
		};

		// --- encuentra encabezados ---
		const findHeaderMap = (sheet, required) => {
			for (let r = 1; r <= sheet.rowCount; r++) {
				const row = sheet.getRow(r);
				const vals = (row.values || []).slice(1).map(normVal);
				if (vals.some((v) => v.trim() !== "")) {
					const map = {};
					required.forEach((req) => {
						const idx = vals.findIndex(
							(h) => h.trim().toLowerCase() === req.toLowerCase()
						);
						map[req] = idx >= 0 ? idx : -1;
					});
					const allFound = Object.values(map).every((i) => i >= 0);
					return { headerRow: r, map, allFound };
				}
			}
			return { headerRow: -1, map: {}, allFound: false };
		};

		// --- lector principal ---
		const parseWorkbookFirstSheet = async (file) => {
			const arrayBuffer = await file.arrayBuffer();
			const workbook = new ExcelJS.Workbook();
			await workbook.xlsx.load(arrayBuffer);

			const sheet = workbook.worksheets[0];
			if (!sheet) return { rowsRaw: [], parts: [], sheetName: "(sin hoja)" };

			const REQUIRED = [
				"PartNum",
				"PartDescription",
				"Qty",
				"UOM",
				"UnitPrice",
				"Amount",
			];
			const { headerRow, map, allFound } = findHeaderMap(sheet, REQUIRED);
			if (!allFound) {
				throw new Error("Faltan encabezados requeridos: " + REQUIRED.join(", "));
			}

			const rowsRaw = [];
			const parts = [];
			const partRejected = [];
			let emptyStreak = 0;

			for (let r = headerRow + 1; r <= sheet.rowCount; r++) {
				const row = sheet.getRow(r);
				const values = (row.values || []).slice(1);
				const trimmed = trimRightEmptyCells(values);
				const allEmpty = trimmed.every(isCellEmpty);

				if (allEmpty) {
					emptyStreak++;
					if (emptyStreak >= 2) break;
					continue;
				} else {
					emptyStreak = 0;
				}

				const get = (name) => normVal(trimmed[map[name]] ?? "");
				const obj = {
					PartNum: get("PartNum"),
					PartDescription: get("PartDescription"),
					Qty: get("Qty"),
					UOM: get("UOM"),
					UnitPrice: get("UnitPrice"),
					//Amount: get('Amount'),
				};
				rowsRaw.push(obj);

				const hasAll = Object.values(obj).every((v) => v.trim() !== "");
				if (hasAll) {
					const dto = new ItemDTO({
						origin: "Excel",
						partNum: typeof obj.PartNum === "string" ? obj.PartNum.toUpperCase() : obj.PartNum,
						description: typeof obj.PartDescription === "string" ? obj.PartDescription.toUpperCase() : obj.PartDescription,
						quantity: parseDecimal(obj.Qty), // 🔹 numérico, sin afectar
						unit: typeof obj.UOM === "string" ? obj.UOM.toUpperCase() : obj.UOM,
						unitPrice: parseDecimal(obj.UnitPrice),
						amount: Number((parseDecimal(obj.UnitPrice) * parseDecimal(obj.Qty)).toPrecision(15))

					});
					// validaciones
					if (dto.quantity < 0 || dto.unitPrice < 1 || dto.amount < 1) {
						partRejected.push(dto);
					} else {
						parts.push(dto);
					}
				} else {
					partRejected.push(obj);
				}
			}

			return { rowsRaw, parts, sheetName: sheet.name || "(sin nombre)" };
		};

		// --- API pública ---
		const readSelectedFileToPartDTOs = async (input) => {
			const file = input.files?.[0];
			if (!file) return { rowsRaw: [], parts: [] };
			const res = await parseWorkbookFirstSheet(file);

			globalVariables.totalAmount = res.parts.reduce((a, p) => a + (p.amount || 0), 0);
			res.noPartRejected = (res.partRejected || []).length;

			return res;
		};

		return { readSelectedFileToPartDTOs };
	})();





	// --- helper: limpiar formulario al crear nuevo ---
	function resetFormNuevo() {
		const $ = (id, v) => {
			const el = document.getElementById(id);
			if (el) el.value = v;
		};
		$("ai_partNum", "");
		$("ai_description", "");
		$("ai_quantity", "1"); // Cantidad se suele dejar en 1
		$("ai_unit", "");

		// Cambiar "0" por "" para que se vea el placeholder
		$("ai_unitPrice", "");

		// El importe se calculará solo, puedes dejarlo en "" o "0" según prefieras
		$("ai_amount", "");
	}

	// --- recolectar datos del formulario ---
	function collectItemFormData() {
		return {
			origin: "Elephant",
			partNum: document.getElementById("ai_partNum").value.trim(),
			description: document.getElementById("ai_description").value.trim(),
			quantity: parseFloat(document.getElementById("ai_quantity").value),
			unit: document.getElementById("ai_unit").value.trim(),
			unitPrice: parseFloat(document.getElementById("ai_unitPrice").value),
			amount: parseFloat(document.getElementById("ai_amount").value),
		};
	}

	// --- validar datos (todos obligatorios y válidos) ---
	function validateItemData(data) {
		const missing = [];
		const invalid = [];

		if (!data.partNum) missing.push("No. de Parte");
		if (!data.description) missing.push("Descripción");
		if (!data.unit) missing.push("Unidad");

		if (!Number.isFinite(data.quantity) || data.quantity < 0)
			invalid.push("Cantidad mayor que 0 unidades");
		if (!Number.isFinite(data.unitPrice) || data.unitPrice < 1)
			invalid.push("Precio Unitario mayor que 0");
		if (!Number.isFinite(data.amount) || data.amount < 1)
			invalid.push("Importe mayor que 0");

		const expected =
			Math.round(Number(data.quantity || 0) * Number(data.unitPrice || 0) * 100) /
			100;
		const sameAmount = Math.abs(expected - Number(data.amount || 0)) < 0.01;

		return {
			ok: missing.length === 0 && invalid.length === 0,
			missing,
			invalid,
			expectedAmount: expected,
			sameAmount,
		};
	}

	// --- calcular importe ---
	function calcularImporte() {
		const qtyEl = document.getElementById("ai_quantity");
		const priceEl = document.getElementById("ai_unitPrice");
		const amtEl = document.getElementById("ai_amount");

		let qty = parseFloat(qtyEl?.value) || 0;
		let price = parseFloat(priceEl?.value) || 0;

		if (qty < 0) {
			qty = 1;
			qtyEl.value = qty;
		}
		if (price < 0) {
			price = 0;
			priceEl.value = price;
		}

		const importe = Math.round(qty * price * 100) / 100;
		amtEl.value = importe.toFixed(2);
	}

	// --- cargar item a modal para edición ---
	function loadPartIntoModal(uuid) {
		isModalInEdition = true;
		uuidEditionElement = uuid;

		let item = localDatabase.getAll().find((p) => p.uuid === uuid);
		if (!item) return;

		document.getElementById("ai_partNum").value = item.partNum ?? "";
		document.getElementById("ai_description").value = item.description ?? "";
		document.getElementById("ai_quantity").value = item.quantity ?? 1;
		document.getElementById("ai_unit").value = item.unit ?? "";
		document.getElementById("ai_unitPrice").value = item.unitPrice ?? 0.0;
		document.getElementById("ai_amount").value = item.amount ?? 0.0;

		modalEdicionParte?.show();
	}

	let paginationListeners = [];

	function attachPaginationClickHandler() {
		// Usamos el contenedor estático de la tabla o el document
		const tableContainer = document.getElementById("ApprovalAccessDistributorsContainer");

		// Removemos listener previo si existiera para evitar duplicados (opcional si controlas bien el flujo)
		// Pero con delegación, solo necesitas ejecutar esto UNA vez al cargar la página, no dentro de updateTable.

		tableContainer.addEventListener('pointerdown', function (event) {
			// Verificamos si el elemento clickeado (o su padre) es un botón de paginación
			const target = event.target.closest('.paginate_button');

			if (target) {
				console.log("Se hizo clic en paginación:", target.textContent);

				// TU LÓGICA AQUÍ
				// Nota: DataTables puede tardar unos milisegundos en renderizar la nueva página.
				// Si necesitas ejecutar algo DESPUÉS del cambio de página, ver Solución 2.
				setTimeout(() => {
					traducirInfo();
					updateCurrencyVisuals();
				}, 100);
			}
		});
	}

	function updateTable() {
		const data = localDatabase.getAll();
		const tableContainer = document.getElementById("ApprovalAccessDistributorsContainer");
		const tableBody = document.getElementById("ApprovalAccessDistributorsbody");
		if (!tableBody) return;

		// 🔹 Calcula el total
		 globalVariables.totalAmount = data.reduce((acc, item) => {
        // Usamos item.amount que ya viene calculado del DTO para mayor precisión
        return acc + (item.amount || 0); 
   	 }, 0);


		initCaseBySwitches();

		// 🔹 Si no hay datos, muestra el mensaje vacío
		if (!Array.isArray(data) || data.length === 0) {
			tableBody.innerHTML = `
            <tr class="sizer-row">
                <td colspan="7" class="text-center text-muted py-3">Sin resultados</td>
            </tr>`;
			return;
		}

		// 🔹 Genera todas las filas
		const rowsHTML = data.map(item => `
        <tr>
            <td>${item.partNum ?? ""}</td>
            <td>${item.description ?? ""}</td>
            <td>${item.quantity ?? ""}</td>
            <td>${item.unit ?? ""}</td>
            <td class="currency">${item.unitPrice ?? ""}</td>
            <td class="currency">${item.amount ?? ""}</td>
            <td>
                <button class="btn btn-sm btn-outline-primary me-1" onpointerdown="loadPartIntoModal('${item.uuid}')">
                    <i class="fa fa-pencil-alt"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" onpointerdown="confirmDelete('${item.partNum}', '${item.uuid}')">
                    <i class="fa fa-trash"></i>
                </button>
            </td>
        </tr>
    `).join('');

		console.log(rowsHTML)

		// 🔹 1. Destruye y elimina completamente cualquier wrapper previo de DataTable
		const tableSelector = '#ApprovalAccessDistributorsTable';
		if ($.fn.DataTable.isDataTable(tableSelector)) {
			$(tableSelector).DataTable().destroy(true);
		}

		// 🔹 2. Limpia el contenedor de DataTable
		const wrapper = document.querySelector('#ApprovalAccessDistributorsTable_wrapper');
		if (wrapper) wrapper.remove();

		// 🔹 3. Recrea la tabla base si fue eliminada por destroy(true)
		if (!document.querySelector(tableSelector)) {
			const newTableHTML = `
            <table id="ApprovalAccessDistributorsTable" class="table table-striped table-bordered" style="width:100%">
                <thead>
                    <tr>
                        <th>No. de Parte</th>
                        <th>Descripción</th>
                        <th>Cantidad</th>
                        <th>Unidad</th>
                        <th>Precio Unitario</th>
                        <th>Importe</th>
                        <th>Acciones</th>
                    </tr>
                    <tr>
                        <th><input type="text" placeholder="Buscar número de parte"></th>
                        <th><input type="text" placeholder="Buscar descripción"></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody id="ApprovalAccessDistributorsbody"></tbody>
            </table>`;
			tableContainer.innerHTML = newTableHTML;
		}

		// 🔹 4. Inserta las nuevas filas
		const tbodyFinal = document.getElementById("ApprovalAccessDistributorsbody");
		if (tbodyFinal) {
			tbodyFinal.innerHTML = rowsHTML;
		} else {
			console.error("No se encontró el tbody 'ApprovalAccessDistributorsbody' tras la reconstrucción.");
		}

		// 🔹 5. Reinicializa DataTable
		initializeDataTable2();

		// 🔹 6. Aplica formato visual
		updateCurrencyVisuals();

		// 🔹 7. Actualiza la info traducida
		traducirInfo();
		// 🔹 Registrar el listener UNA SOLA VEZ (no se duplica)
		attachPaginationClickHandler();
	}

	/**
 * Inicializa, o reinicializa, la tabla de líneas de cotización con DataTables.
 */
function initializeDataTable2() {
    const tableSelector = '#ApprovalAccessDistributorsTable';

    // 🔹 1. Destruir la instancia previa
    if ($.fn.DataTable.isDataTable(tableSelector)) {
        $(tableSelector).DataTable().destroy();
        $('#ApprovalAccessDistributorsTable thead tr:eq(1) th input').val('');
    }

    // 🔹 2. Inicializar DataTable
    const table = $(tableSelector).DataTable({
        responsive: true,
        scrollX: true,
        dom: 'Blfrtip',
        buttons: [
            {
                // CAMBIO 1: Usar 'excelHtml5' para asegurar compatibilidad sin Flash
                extend: 'excelHtml5', 
                text: 'Exportar a Excel',
                className: 'btn btn-success',
                exportOptions: {
                    // CAMBIO 2: Quitar ':visible'. Exportar todo menos la última columna (Acciones)
                    columns: ':not(:last-child)', 
                    format: {
                        header: function (data, columnIdx) {
                            let headerSelector = $('#ApprovalAccessDistributorsTable_wrapper .dataTables_scrollHead thead tr:first-child th');
                            if (headerSelector.length === 0) {
                                headerSelector = $('#ApprovalAccessDistributorsTable thead tr:first-child th');
                            }
                            return headerSelector.eq(columnIdx).text();
                        }
                    }
                }
            },
            {
                // CAMBIO 1: Usar 'pdfHtml5'
                extend: 'pdfHtml5', 
                text: 'Exportar a PDF',
                className: 'btn btn-danger',
                orientation: 'landscape',
                pageSize: 'LEGAL',
                exportOptions: {
                    // CAMBIO 2: Quitar ':visible'. 
                    columns: ':not(:last-child)',
                    format: {
                        header: function (data, columnIdx) {
                            let headerSelector = $('#ApprovalAccessDistributorsTable_wrapper .dataTables_scrollHead thead tr:first-child th');
                            if (headerSelector.length === 0) {
                                headerSelector = $('#ApprovalAccessDistributorsTable thead tr:first-child th');
                            }
                            return headerSelector.eq(columnIdx).text();
                        }
                    }
                }
            }
        ],
        pageLength: 10,
        searching: true,
        ordering: true,
        lengthMenu: [[10, 25, 50, -1], [10, 25, 50, "Todos"]],
        columnDefs: [
            {
                targets: -1, 
                orderable: false,
                searchable: false
            }
        ],
        order: [[0, 'asc']],
        initComplete: function () {
            this.api().columns().every(function () {
                const column = this;
                const input = $('input', $(column.header()).parent().parent().find('tr:eq(1) th').eq(column.index()));
                if (input.length > 0) {
                    input.off('keyup change').on('keyup change', function () {
                        if (column.search() !== this.value) {
                            column.search(this.value).draw();
                        }
                    });
                }
            });
        }
    });

    return table;
}


	function updateCurrencyVisuals() {
		const formatter = new Intl.NumberFormat("es-MX", {
			minimumFractionDigits: 3,
			maximumFractionDigits: 3
		});

		document.querySelectorAll(".currency").forEach(td => {
			const rawText = td.textContent.trim();
			const num = parseFloat(rawText.replace(/[^\d.-]/g, ""));
			if (isNaN(num)) return;

			// Mantenemos el valor real
			td.dataset.realValue = rawText;

			// Mostramos solo la versión formateada
			td.textContent = formatter.format(num);
		});
	}




	function confirmDelete(partNum, uuid) {
		Swal.fire({
			title: `¿Deseas eliminar el ítem <u>${partNum}</u>?`,
			icon: "warning",
			html: `<p>Se eliminará este registro.</p><p class="text-danger fw-bold">Esta acción no se puede deshacer.</p>`,
			showCloseButton: true,
			showCancelButton: true,
			focusConfirm: true,
			confirmButtonText: `<i class="fa fa-trash"></i> Sí, eliminar`,
			cancelButtonText: `<i class="fa fa-times"></i> No, cancelar`,
		}).then((r) => {
			if (r.isConfirmed) {
				localDatabase.remove(uuid);
				updateTable();
				Swal.fire({
					icon: "success",
					title: "Eliminado",
					timer: 2000,
					showConfirmButton: false,
				});
			}
		});
	}


	/**
	 * Llama a la API para obtener las monedas y llena el select de Moneda.
	 */
	async function fillCurrencySelect() {
		const currencySelect = document.getElementById("Finance_CurrencyType");
		currencySelect.innerHTML = '<option value="">Seleccione una opción</option>';
		try {
			const response = await axios.get("/api/Operaciones/Ingenieria/Cotiz/CotizCreatedIng/currencies");
			const currencies = response.data;
			if (currencies && currencies.length > 0) {
				currencies.forEach((currency) => {
					const option = document.createElement("option");
					option.value = currency.CurrencyCode;
					option.textContent = currency.FrontES;
					currencySelect.appendChild(option);
				});
			}
		} catch (error) {
			console.error("Error al obtener las monedas:", error);
		}
	}


	async function fillTaxCurrencySelect(currencyCode) {
		const taxCurrencySelect = document.getElementById("Finance_TaxCurrencyType");
		taxCurrencySelect.innerHTML = '<option value="">Seleccione una opción</option>';
		try {
			const response = await axios.get(`/Ingenieria/Cotiz/CotizCreatedIng/get_taxes?currency=${currencyCode}`);
			const currencies = response.data;
			if (currencies && currencies.length > 0) {
				currencies.forEach((currency) => {
					const option = document.createElement("option");
					option.value = currency.TaxCode;
					option.textContent = currency.FrontES;
					taxCurrencySelect.appendChild(option);
				});
			}
		} catch (error) {
			console.error("Error al obtener las monedas:", error);
		}
	}

	document.addEventListener("DOMContentLoaded", () => {

		// Funciones al iniciar ----------------------------------------------
		fillCurrencySelect();

		// Eventos ----------------------------------------------
		const select = document.getElementById("Finance_CurrencyType");
		if (select) {
			select.addEventListener("change", (event) => {
				fillTaxCurrencySelect(event.target.value);
			});
		}



		validador = new buttonSendCotiz()
		const btnEnviar = document.getElementById("boton-final-enviar");

		// Validación y envío
		btnEnviar.addEventListener("pointerdown", () => {
			enviarArchivosCotizCreatedIng();
		});

		downloadFormat = document.getElementById("downloadFormat");
		if (downloadFormat) {
			downloadFormat.addEventListener("pointerdown", downloadFile);
		}

		fileInput = document.getElementById("fileInput");
		fileInput.addEventListener("change", async (event) => {
			const file = event.target.files[0]; // primer archivo seleccionado
			if (file) {
				try {
					const { rowsRaw, parts, sheetName, noPartRejected } =
						await ExcelJson.readSelectedFileToPartDTOs(fileInput);
					let localDb = new LocalDb();
					if (parts.length > 0) {
						Swal.fire({
							title: "Importación exitosa",
							html: `
							<ul style="text-align:left;">
							<li><b>No de conceptos agregados:</b> ${parts.length}</li>
							<li><b>Importe total:</b> $${globalVariables.totalAmount.toFixed(2)}</li>
							</ul>
							`,
							icon: "success",
						});
						localDb.addItems(parts);
						updateTable();
					} else {
						Swal.fire({
							title: "No se importaron partes",
							text: `No se importaron partes. Verifica que el archivo tenga datos válidos.`,
							icon: "warning",
						});
					}
				} catch (err) {
					console.error(err);
				} finally {
					// Limpia el input para permitir re-seleccionar el mismo archivo si quieres
					fileInput.value = "";
				}
				// 👉 Aquí puedes lanzar tu función de importación
			} else {
				console.log("No se seleccionó ningún archivo");
			}
		});

		const modalEl = document.getElementById("editItemModal");
		if (modalEl) {
			modalEdicionParte = new bootstrap.Modal(modalEl, {
				backdrop: true,
				keyboard: true,
				focus: true,
			});
			modalEl.addEventListener("hidden.bs.modal", () => {
				isModalInEdition = false;
				btn_agregar?.focus();
			});
		}

		// Listeners de cálculo
		const qtyEl = document.getElementById("ai_quantity");
		const priceEl = document.getElementById("ai_unitPrice");
		qtyEl?.addEventListener("input", calcularImporte, { passive: true });
		priceEl?.addEventListener("input", calcularImporte, { passive: true });
		qtyEl?.addEventListener("blur", calcularImporte);
		priceEl?.addEventListener("blur", calcularImporte);

		// Guardar (crear/editar) con VALIDACIÓN y **SweetAlert2**
		document.getElementById("btn_agregar2")?.addEventListener("pointerup", () => {
			calcularImporte(); // asegura amount actualizado
			const data = collectItemFormData();
			const { ok, missing, invalid, expectedAmount, sameAmount } =
				validateItemData(data);

			if (!ok || !sameAmount) {
				// Build HTML para Swal
				const li = (arr) => arr.map((x) => `<li>${x}</li>`).join("");
				const missingHtml = missing.length
					? `<h6 class="mt-2 mb-1">Falta:</h6><ul>${li(missing)}</ul>`
					: "";
				const invalidHtml = invalid.length
					? `<h6 class="mt-2 mb-1">Revisa:</h6><ul>${li(invalid)}</ul>`
					: "";
				const tipHtml = !sameAmount
					? `<p class="mt-2"><b>Tip:</b> El Importe esperado es <code>${expectedAmount.toFixed(
						2
					)}</code> (Cantidad × Precio).</p>`
					: "";

				const html = `
					<div style="text-align:left">
					<p>Por favor completa la información requerida.</p>
					${missingHtml}
					${invalidHtml}
					${tipHtml}
					</div>
				`;

				Swal.fire({
					title: "Falta información",
					html,
					icon: "error",
					confirmButtonText: "Entendido",
				}).then(() => {
					const mapFieldToId = {
						"No. de Parte": "ai_partNum",
						Descripción: "ai_description",
						Unidad: "ai_unit",
						"Cantidad (> 0)": "ai_quantity",
						"Precio Unitario (≥ 0)": "ai_unitPrice",
						"Importe (≥ 0)": "ai_amount",
					};
					const first = missing[0] || invalid[0] || null;
					if (first && mapFieldToId[first])
						document.getElementById(mapFieldToId[first])?.focus();

					if (!ok && !sameAmount) {
						document.getElementById("ai_amount").value =
							expectedAmount.toFixed(2);
					}
				});

				if (!ok) return;
				if (!sameAmount) {
					data.amount = expectedAmount;
					document.getElementById("ai_amount").value = expectedAmount.toFixed(2);
				}
			}

			// Asignar UUID si es edición
			data.uuid = isModalInEdition ? uuidEditionElement : null;

			const item = new ItemDTO(data);

			if (isModalInEdition) {
				localDatabase.update(item);
			} else {
				localDatabase.add(item);
			}

			isModalInEdition = false;
			modalEdicionParte?.hide();
			updateTable();
		});
	});

	// Abrir modal para nuevo
	btn_agregar.addEventListener("pointerdown", () => {
		isModalInEdition = false;
		resetFormNuevo();
		modalEdicionParte?.show();
	});

	document.addEventListener("DOMContentLoaded", () => {
		localDatabase.clear();
		updateTable();
	});

	document.addEventListener('DOMContentLoaded', async function () {


		if (window.customElements?.whenDefined) {
			await customElements.whenDefined('upload-file-component');
		}

		const uploader = document.getElementById('uploader');
		if (!uploader) {
			console.error('No se encontró #uploader');
			return;
		}

		const reflectToGlobal = (ev) => {
			const { files = [], plainFiles = [], reason, count } = ev.detail || {};
			// 💾 Reemplaza TODO el estado global con la lista completa
			globalVariables.setAllFiles(files, plainFiles);
		};

		// Cualquiera de los dos; ambos emiten lista completa en tu componente
		uploader.addEventListener('filesselected', reflectToGlobal);
		globalVariables.clearFiles();
	});

	/* === Funcionalidad formulario costeo === */
	/**
	 * @class CostingForm
	 * @description Clase base con correcciones para manejo de AutoNumeric.
	 */
	class CostingForm {
		typeCase: string;
		title: HTMLElement;
		switchNeodataOrigin: HTMLInputElement;
		switchTender: HTMLInputElement;

		// Elementos DOM
		_inputDirectCost: HTMLInputElement;
		_inputIndirectValue: HTMLInputElement;
		_inputIndirectPercent: HTMLInputElement;
		_inputFinancingValue: HTMLInputElement;
		_inputFinancingPercent: HTMLInputElement;
		_inputProfitValue: HTMLInputElement;
		_inputProfitPercent: HTMLInputElement;
		_inputProposedAmountValue: HTMLInputElement;
		_inputOperatingExpensesValue: HTMLInputElement;
		_inputOperatingExpensesPercent: HTMLInputElement;
		_inputMinSalePrice: HTMLInputElement;
		_inputMaxDiscountValue: HTMLInputElement;
		_inputMaxDiscountPercent: HTMLInputElement;
		_inputListPrice: HTMLInputElement;

		allInputs: HTMLInputElement[];
		sequentialInputs: any;
		listeners: any;

		// Instancias de AutoNumeric
		_inputDirectCost_log: AutoNumeric;
		_inputIndirectValue_log: AutoNumeric;
		_inputFinancingValue_log: AutoNumeric;
		_inputProfitValue_log: AutoNumeric;
		_inputProposedAmountValue_log: AutoNumeric;
		_inputOperatingExpensesValue_log: AutoNumeric;
		_inputMinSalePrice_log: AutoNumeric;
		_inputMaxDiscountValue_log: AutoNumeric;
		_inputListPrice_log: AutoNumeric;
		_inputIndirectPercent_log: AutoNumeric;
		_inputFinancingPercent_log: AutoNumeric;
		_inputProfitPercent_log: AutoNumeric;
		_inputOperatingExpensesPercent_log: AutoNumeric;
		_inputMaxDiscountPercent_log: AutoNumeric;

		constructor() {
			this.typeCase = "";
			this.title = document.getElementById("costing-case-title");
			this.switchNeodataOrigin = document.getElementById("switchNeodataOrigin") as HTMLInputElement;
			this.switchTender = document.getElementById("switchTender") as HTMLInputElement;

			// Asignación de elementos DOM
			this._inputDirectCost = document.getElementById("inputDirectCost") as HTMLInputElement;
			this._inputIndirectValue = document.getElementById("inputIndirectValue") as HTMLInputElement;
			this._inputIndirectPercent = document.getElementById("inputIndirectPercent") as HTMLInputElement;
			this._inputFinancingValue = document.getElementById("inputFinancingValue") as HTMLInputElement;
			this._inputFinancingPercent = document.getElementById("inputFinancingPercent") as HTMLInputElement;
			this._inputProfitValue = document.getElementById("inputProfitValue") as HTMLInputElement;
			this._inputProfitPercent = document.getElementById("inputProfitPercent") as HTMLInputElement;
			this._inputMinSalePrice = document.getElementById("inputMinSalePrice") as HTMLInputElement;
			this._inputOperatingExpensesValue = document.getElementById("inputOperatingExpensesValue") as HTMLInputElement;
			this._inputOperatingExpensesPercent = document.getElementById("inputOperatingExpensesPercent") as HTMLInputElement;
			this._inputMaxDiscountValue = document.getElementById("inputMaxDiscountValue") as HTMLInputElement;
			this._inputMaxDiscountPercent = document.getElementById("inputMaxDiscountPercent") as HTMLInputElement;
			this._inputListPrice = document.getElementById("inputListPrice") as HTMLInputElement;
			this._inputProposedAmountValue = document.getElementById("inputProposedAmountValue") as HTMLInputElement;

			this.allInputs = [
				this._inputDirectCost, this._inputIndirectValue, this._inputIndirectPercent,
				this._inputFinancingValue, this._inputFinancingPercent, this._inputProfitValue,
				this._inputProfitPercent, this._inputOperatingExpensesValue, this._inputOperatingExpensesPercent,
				this._inputMaxDiscountValue, this._inputMaxDiscountPercent, this._inputMinSalePrice,
				this._inputListPrice, this._inputProposedAmountValue,
			];

			this.applyCurrencyType();
			this.initSwitchBehavior();
			// this.bindAutoNumericKeyPress(); // AutoNumeric ya maneja esto internamente usualmente
		}

		applyCurrencyType() {
			const configCurrency = { decimalPlaces: 2, roundingMethod: "S" }; // Opcional: currencySymbol: '$ '
			const configPercent = { decimalPlaces: 2, roundingMethod: "S", minimumValue: "0", maximumValue: "100", suffixText: "%" };

			this._inputDirectCost_log = new AutoNumeric(this._inputDirectCost, configCurrency);
			this._inputIndirectValue_log = new AutoNumeric(this._inputIndirectValue, configCurrency);
			this._inputFinancingValue_log = new AutoNumeric(this._inputFinancingValue, configCurrency);
			this._inputProfitValue_log = new AutoNumeric(this._inputProfitValue, configCurrency);
			this._inputProposedAmountValue_log = new AutoNumeric(this._inputProposedAmountValue, configCurrency);
			this._inputOperatingExpensesValue_log = new AutoNumeric(this._inputOperatingExpensesValue, configCurrency);
			this._inputMinSalePrice_log = new AutoNumeric(this._inputMinSalePrice, configCurrency);
			this._inputMaxDiscountValue_log = new AutoNumeric(this._inputMaxDiscountValue, configCurrency);
			this._inputListPrice_log = new AutoNumeric(this._inputListPrice, configCurrency);

			this._inputIndirectPercent_log = new AutoNumeric(this._inputIndirectPercent, configPercent);
			this._inputFinancingPercent_log = new AutoNumeric(this._inputFinancingPercent, configPercent);
			this._inputProfitPercent_log = new AutoNumeric(this._inputProfitPercent, configPercent);
			this._inputOperatingExpensesPercent_log = new AutoNumeric(this._inputOperatingExpensesPercent, configPercent);
			this._inputMaxDiscountPercent_log = new AutoNumeric(this._inputMaxDiscountPercent, configPercent);
		}

		// --- Setters (Para escribir en el input y que se formatee) ---
		// Usamos .set() que recibe un número y lo formatea
		set inputDirectCost(val) { this._inputDirectCost_log.set(val); }
		set inputIndirectValue(val) { this._inputIndirectValue_log.set(val); }
		set inputFinancingValue(val) { this._inputFinancingValue_log.set(val); }
		set inputProfitValue(val) { this._inputProfitValue_log.set(val); }
		set inputProposedAmountValue(val) { this._inputProposedAmountValue_log.set(val); }
		set inputOperatingExpensesValue(val) { this._inputOperatingExpensesValue_log.set(val); }
		set inputMinSalePrice(val) { this._inputMinSalePrice_log.set(val); }
		set inputMaxDiscountValue(val) { this._inputMaxDiscountValue_log.set(val); }
		set inputListPrice(val) { this._inputListPrice_log.set(val); }

		set inputIndirectPercent(val) { this._inputIndirectPercent_log.set(val); }
		set inputFinancingPercent(val) { this._inputFinancingPercent_log.set(val); }
		set inputProfitPercent(val) { this._inputProfitPercent_log.set(val); }
		set inputOperatingExpensesPercent(val) { this._inputOperatingExpensesPercent_log.set(val); }
		set inputMaxDiscountPercent(val) { this._inputMaxDiscountPercent_log.set(val); }

		// --- Getters (Para mostrar texto si se requiere, NO para cálculos matemáticos) ---
		get inputDirectCost() { return this._inputDirectCost_log.getFormatted(); }
		// ... (Puedes dejar los getters si los usas para logs o UI textual, pero no para sumas)

		/**
		 * Helper para obtener valor numérico limpio.
		 * Si está vacío devuelve 0.
		 */
		getNum(logInstance: AutoNumeric): number {
			// getNumber() devuelve un number real. Si es null, retorna 0.
			return logInstance.getNumber() || 0;
		}

		toJson() {
			return {
				directCost: this._inputDirectCost_log.getNumber() || 0,
				indirect: {
					amount: this._inputIndirectValue_log.getNumber() || 0,
					percent: this._inputIndirectPercent_log.getNumber() || 0,
				},
				financing: {
					amount: this._inputFinancingValue_log.getNumber() || 0,
					percent: this._inputFinancingPercent_log.getNumber() || 0,
				},
				profit: {
					amount: this._inputProfitValue_log.getNumber() || 0,
					percent: this._inputProfitPercent_log.getNumber() || 0,
				},
				proposalAmount: this._inputProposedAmountValue_log.getNumber() || 0,
				operatingExpenses: {
					amount: this._inputOperatingExpensesValue_log.getNumber() || 0,
					percent: this._inputOperatingExpensesPercent_log.getNumber() || 0,
				},
				minSalePrice: this._inputMinSalePrice_log.getNumber() || 0,
				maxDiscount: {
					amount: this._inputMaxDiscountValue_log.getNumber() || 0,
					percent: this._inputMaxDiscountPercent_log.getNumber() || 0,
				},
				listPrice: this._inputListPrice_log.getNumber() || 0,
				TaxCode: (document.getElementById("Finance_TaxCurrencyType") as HTMLSelectElement).value,
				CurrencyCode: (document.getElementById("Finance_CurrencyType") as HTMLSelectElement).value
			};
		}

		getValues() {
			return {
				directCost: this.getNum(this._inputDirectCost_log),
				indirectValue: this.getNum(this._inputIndirectValue_log),
				indirectPercent: this.getNum(this._inputIndirectPercent_log),
				financingValue: this.getNum(this._inputFinancingValue_log),
				financingPercent: this.getNum(this._inputFinancingPercent_log),
				profitValue: this.getNum(this._inputProfitValue_log),
				profitPercent: this.getNum(this._inputProfitPercent_log),
				minSalePrice: this.getNum(this._inputMinSalePrice_log),
				operatingExpensesValue: this.getNum(this._inputOperatingExpensesValue_log),
				operatingExpensesPercent: this.getNum(this._inputOperatingExpensesPercent_log),
				maxDiscountValue: this.getNum(this._inputMaxDiscountValue_log),
				maxDiscountPercent: this.getNum(this._inputMaxDiscountPercent_log),
				listPrice: this.getNum(this._inputListPrice_log),
				neodataOrigin: this.switchNeodataOrigin.checked,
				tender: this.switchTender.checked,
				ProposedAmount: this.getNum(this._inputProposedAmountValue_log),
			};
		}

		detachAutoNumeric() {
			const logs = [
				this._inputDirectCost_log,
				this._inputIndirectValue_log,
				this._inputFinancingValue_log,
				this._inputProfitValue_log,
				this._inputProposedAmountValue_log,
				this._inputOperatingExpensesValue_log,
				this._inputMinSalePrice_log,
				this._inputMaxDiscountValue_log,
				this._inputListPrice_log,
				this._inputIndirectPercent_log,
				this._inputFinancingPercent_log,
				this._inputProfitPercent_log,
				this._inputOperatingExpensesPercent_log,
				this._inputMaxDiscountPercent_log
			];

			logs.forEach(log => {
				// Verificamos si existe y si tiene el método remove (AutoNumeric v4+)
				if (log && typeof log.remove === 'function') {
					log.remove();
				}
			});
		}

		updateProposedAmount() {
			const direct = this.getNum(this._inputDirectCost_log);
			const indirect = this.getNum(this._inputIndirectValue_log);
			const financing = this.getNum(this._inputFinancingValue_log);
			const profit = this.getNum(this._inputProfitValue_log);
			const proposedAmount = direct + indirect + financing + profit;
			this.inputProposedAmountValue = proposedAmount; // Setter maneja el formato
		}

		initSwitchBehavior() {
			const updateTenderSwitch = () => {
				this.switchTender.disabled = !this.switchNeodataOrigin.checked;
				if (this.switchTender.disabled) {
					this.switchTender.checked = false;
				}
			};
			updateTenderSwitch();
			this.switchNeodataOrigin.addEventListener("change", updateTenderSwitch);
		}

		resetInputs() {
			this.allInputs.forEach((input) => {
				if (input) {
					input.value = "";
					input.disabled = true;
				}
			});
			// Limpieza profunda de AutoNumeric
			[
				this._inputDirectCost_log, this._inputIndirectValue_log, this._inputFinancingValue_log,
				this._inputProfitValue_log, this._inputProposedAmountValue_log, this._inputOperatingExpensesValue_log,
				this._inputMinSalePrice_log, this._inputMaxDiscountValue_log, this._inputListPrice_log,
				this._inputIndirectPercent_log, this._inputFinancingPercent_log, this._inputProfitPercent_log,
				this._inputOperatingExpensesPercent_log, this._inputMaxDiscountPercent_log
			].forEach(log => log && log.clear());
		}

		attachNonNegativeValidation() {
			// AutoNumeric ya impide negativos si configuras `minimumValue: '0'`, 
			// pero mantenemos esto por compatibilidad con tu lógica de deshabilitar inputs.
			this.allInputs.forEach((input) => {
				if (input) {
					input.addEventListener("input", (e: any) => {
						// Validar usando el valor numérico, no el string
						// Pero como AutoNumeric bloquea teclas invalidas, esto es principalmente para lógica de negocio
					});
				}
			});
		}

		// validaciones se implementan en las clases hijas
		isValidSum(): buttonSendCotiz_StatusValidationMessageDTO { return { isValid: true, message: "" }; }
		isValidSalePrice(): buttonSendCotiz_StatusValidationMessageDTO { return { isValid: true, message: "" }; }
		isValidSpecificRules(): buttonSendCotiz_StatusValidationMessageDTO { return { isValid: true, message: "" }; }
	}

	/**
	 * CASE A: Neodata: true, Tender: true
	 */
	class CaseA extends CostingForm {
		constructor() {
			super();
			this.typeCase = "A";
			this.title.textContent = "Cálculo Financiero";
			this.listeners = [];
			this.applyCaseARules();
			this.setInitialPrice();
			this.initListeners();
			// this.attachNonNegativeValidation(); // AutoNumeric ya maneja esto
			this.initPercentFromValues();
		}

		applyCaseARules() {
			this.allInputs.forEach((input) => (input.disabled = true));
			this._inputDirectCost.disabled = false;
		}

		setInitialPrice() {
			if (typeof globalVariables.totalAmount !== "undefined") {
				// Usamos el setter para formatear
				this.inputMinSalePrice = globalVariables.totalAmount;
			}
		}

		initListeners() {
			const valueInputs = [
				this._inputDirectCost,
				this._inputIndirectValue,
				this._inputFinancingValue,
				this._inputProfitValue,
			];

			const inputHandler = (event) => {
				const currentInput = event.target;
				const currentIndex = valueInputs.indexOf(currentInput);
				const nextInput = valueInputs[currentIndex + 1];

				// AutoNumeric 'input' event da el valor formateado en .value, 
				// pero checkSumLimit usará .getNumber() internamente.
				const isValid = this.checkSumLimit();

				if (!isValid) {
					// Limpiar usando el objeto log asociado
					// Truco: encontrar el log correspondiente al input
					const logs = [this._inputDirectCost_log, this._inputIndirectValue_log, this._inputFinancingValue_log, this._inputProfitValue_log];
					logs[currentIndex].clear();

					for (let i = currentIndex + 1; i < valueInputs.length; i++) {
						if (valueInputs[i]) {
							valueInputs[i].value = "";
							valueInputs[i].disabled = true;
						}
					}
					return;
				}

				// Habilitar siguiente si hay valor numérico válido
				const currentVal = logs[currentIndex].getNumber();
				if (currentVal !== null && currentVal >= 0) {
					if (nextInput) nextInput.disabled = false;
				}
			};

			// Necesitamos mapear inputs a logs para el handler anterior
			const logs = [this._inputDirectCost_log, this._inputIndirectValue_log, this._inputFinancingValue_log, this._inputProfitValue_log];

			valueInputs.forEach((input, index) => {
				if (input) {
					input.addEventListener("input", inputHandler); // 'input' funciona bien con AutoNumeric v4+
					this.listeners.push({ element: input, eventType: "input", handler: inputHandler });
				}
			});

			// Validación final diferida
			let debounceTimeout;
			this._inputProfitValue.addEventListener("input", () => {
				clearTimeout(debounceTimeout);
				debounceTimeout = setTimeout(() => {
					if (this._inputProfitValue_log.getNumber() !== null) {
						this.showFinalBalanceMessage();
					}
				}, 3000);
			});
		}

		checkSumLimit() {
			// CORRECCIÓN CLAVE: Usar getNum() en lugar de parseFloat(this.input...)
			const direct = this.getNum(this._inputDirectCost_log);
			const indirect = this.getNum(this._inputIndirectValue_log);
			const financing = this.getNum(this._inputFinancingValue_log);
			const profit = this.getNum(this._inputProfitValue_log);
			const minSalePrice = this.getNum(this._inputMinSalePrice_log);

			const sumaComponentes = direct + indirect + financing + profit;
			const tolerance = 0.01;

			if (!minSalePrice || minSalePrice <= 0) {
				Swal.fire({
					icon: "warning",
					title: "Advertencia",
					text: "La suma de los costos no puede exceder el importe del precio de venta.",
				});
				return false;
			}

			if (sumaComponentes - minSalePrice > tolerance) {
				Swal.fire({
					icon: "warning",
					title: "Advertencia: Montos incorrectos",
					html: `La suma de costos (${sumaComponentes.toFixed(2)}) excede el precio de venta (${minSalePrice.toFixed(2)})`,
					timer: 3000
				});
				return false;
			}

			return true;
		}

		showFinalBalanceMessage() {
			const direct = this.getNum(this._inputDirectCost_log);
			const indirect = this.getNum(this._inputIndirectValue_log);
			const financing = this.getNum(this._inputFinancingValue_log);
			const profit = this.getNum(this._inputProfitValue_log);
			const minSalePrice = this.getNum(this._inputMinSalePrice_log);

			const sumaComponentes = direct + indirect + financing + profit;
			const tolerance = 0.01;

			if (Math.abs(sumaComponentes - minSalePrice) > tolerance) {
				Swal.fire({
					icon: "error",
					title: "Montos incorrectos",
					text: "La suma de los costos está por debajo del precio de venta."
				});
				this._inputProfitValue_log.clear();
			}
		}

		initPercentFromValues() {
			const valueInputs = [
				this._inputIndirectValue,
				this._inputFinancingValue,
				this._inputProfitValue,
				this._inputOperatingExpensesValue,
				this._inputMaxDiscountValue,
			];
			const handler = () => this.updatePercentFromValues();
			valueInputs.forEach((input) => {
				if (input) {
					input.addEventListener("input", handler);
					this.listeners.push({ element: input, eventType: "input", handler });
				}
			});
		}

		updatePercentFromValues() {
			// CORRECCIÓN: Usar getNum()
			const direct = this.getNum(this._inputDirectCost_log);
			const indirectValue = this.getNum(this._inputIndirectValue_log);
			const financingValue = this.getNum(this._inputFinancingValue_log);
			const profitValue = this.getNum(this._inputProfitValue_log);

			if (direct === 0) return;

			const indirectPercent = (indirectValue / direct) * 100;
			const financingPercent = (financingValue / (direct + indirectValue)) * 100;
			const profitPercent = (profitValue / (direct + indirectValue + financingValue)) * 100;

			// Usar setters para actualizar la vista
			this.inputIndirectPercent = indirectPercent;
			this.inputFinancingPercent = financingPercent;
			this.inputProfitPercent = profitPercent;
		}

		destroy() {
			// 1. Remover listeners nativos (tu código actual)
			this.listeners.forEach(({ element, eventType, handler }) => {
				element.removeEventListener(eventType, handler);
			});
			this.listeners = [];

			// 2. NUEVO: Remover AutoNumeric para evitar duplicidad
			this.detachAutoNumeric();
		}

		isValidSum() {
			const directCost = round2(this.getNum(this._inputDirectCost_log)) || 0;
			const indirectCost = round2(this.getNum(this._inputIndirectValue_log)) || 0;
			const financingCost = round2(this.getNum(this._inputFinancingValue_log)) || 0;
			const profitCost = round2(this.getNum(this._inputProfitValue_log)) || 0;
			const operatingExpensesCost = round2(this.getNum(this._inputOperatingExpensesValue_log)) || 0;
			const precioVenta = round2(this.getNum(this._inputMinSalePrice_log)) || 0; // ✅ CORRECCIÓN: Declarado aquí

			if ((directCost + indirectCost + financingCost + profitCost + operatingExpensesCost) !== precioVenta) {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "La suma de los costos no coincide con el precio de venta");
			}
			else {
				return new buttonSendCotiz_StatusValidationMessageDTO(true, "")
			}
		}

		isValidSalePrice(): buttonSendCotiz_StatusValidationMessageDTO {
			const precioVenta = round2(this.getNum(this._inputMinSalePrice_log)) || 0;
			if (Number(precioVenta) <= 0) {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "Precio de venta inválido");
			}
			else {
				return new buttonSendCotiz_StatusValidationMessageDTO(true, "");
			}
		}

	}

	/**
	 * CASE B: Neodata: false
	 */
	class CaseB extends CostingForm {
		constructor() {
			super();
			this.typeCase = "B";
			this.title.textContent = "Cálculo Financiero";
			this.listeners = [];
			this.applyCaseBRules();
			this.setInitialDirectCost();
			this.initSequentialInputs();
			this.updateCalculations();
		}

		updateDirectCost() {
			if (typeof globalVariables.totalAmount !== "undefined") {
				this.inputDirectCost = globalVariables.totalAmount;
				this.inputMinSalePrice = globalVariables.totalAmount;
				this.inputListPrice = globalVariables.totalAmount;
				this.inputProposedAmountValue = globalVariables.totalAmount;
			}
		}

		setInitialDirectCost() {
			if (typeof globalVariables.totalAmount !== "undefined") {
				this.inputDirectCost = globalVariables.totalAmount;
				this._inputDirectCost.disabled = true;
			}
		}

		applyCaseBRules() {
			this.allInputs.forEach((input) => (input.disabled = true));
			this._inputDirectCost.disabled = false;
			this._inputIndirectPercent.disabled = false;
		}

		initSequentialInputs() {
			const sequentialOrder = [
				this._inputIndirectPercent,
				this._inputFinancingPercent,
				this._inputProfitPercent,
				this._inputOperatingExpensesPercent,
				this._inputMaxDiscountPercent,
			];

			const inputHandler = (event) => {
				const currentIndex = sequentialOrder.indexOf(event.target);
				const nextInput = sequentialOrder[currentIndex + 1];

				if (event.target === this._inputIndirectPercent) {
					const directValue = this.getNum(this._inputDirectCost_log);
					if (!directValue || directValue <= 0) {
						Swal.fire({ icon: "warning", text: "Tu catálogo de conceptos está vacío" });
						this._inputIndirectPercent_log.clear();
						return;
					}
				}

				const isValidCalc = this.updateCalculations();
				const isValidPercent = this.validateTotalPercent();

				if (!isValidCalc || !isValidPercent) {
					// Borramos el valor inválido usando AutoNumeric
					const logMap = [this._inputIndirectPercent_log, this._inputFinancingPercent_log, this._inputProfitPercent_log, this._inputOperatingExpensesPercent_log, this._inputMaxDiscountPercent_log];
					logMap[currentIndex].clear();

					for (let i = currentIndex + 1; i < sequentialOrder.length; i++) {
						sequentialOrder[i].value = "";
						sequentialOrder[i].disabled = true;
					}
					return;
				}

				if (nextInput) nextInput.disabled = false;
			};

			sequentialOrder.forEach((input) => {
				if (input) {
					input.addEventListener("input", inputHandler);
					this.listeners.push({ element: input, eventType: "input", handler: inputHandler });
				}
			});
		}

		updateCalculations() {

			// 🔹 Obtener números crudos pero redondeados desde el inicio
			const direct = round2(this.getNum(this._inputDirectCost_log));
			const indirectPercent = round2(this.getNum(this._inputIndirectPercent_log));

			// 🔹 Cálculos paso a paso, todos redondeados
			const indirectValue = round2((direct * indirectPercent) / 100);
			const subtotal1 = round2(direct + indirectValue);

			const financingPercent = round2(this.getNum(this._inputFinancingPercent_log));
			const financingValue = round2((subtotal1 * financingPercent) / 100);
			const subtotal2 = round2(subtotal1 + financingValue);

			const profitPercent = round2(this.getNum(this._inputProfitPercent_log));
			const profitValue = round2((subtotal2 * profitPercent) / 100);
			const subtotal3 = round2(subtotal2 + profitValue);

			const opPercent = round2(this.getNum(this._inputOperatingExpensesPercent_log));
			const opValue = round2((subtotal3 * opPercent) / 100);
			const salePrice = round2(subtotal3 + opValue);

			const discountPercent = round2(this.getNum(this._inputMaxDiscountPercent_log));
			const discountValue = round2((salePrice * discountPercent) / 100);

			let listPrice = 0;
			if (discountPercent < 100) {
				const divisor = 1 - (discountPercent / 100);
				listPrice = round2(salePrice / divisor);
			}

			// 🔹 Setters con valores ya redondeados
			this.inputIndirectValue = indirectValue;
			this.inputFinancingValue = financingValue;
			this.inputProfitValue = profitValue;
			this.inputOperatingExpensesValue = opValue;
			this.inputMinSalePrice = salePrice;
			this.inputMaxDiscountValue = discountValue;
			this.inputListPrice = listPrice;

			// 🔹 Validación
			if (opPercent >= 100) {
				Swal.fire({
					icon: "warning",
					text: `El porcentaje de gastos de operación no puede superar el 100%.`,
				});
				return false;
			}

			this.updateProposedAmount();
			return true;
		}


		validateTotalPercent() {
			const indirect = this.getNum(this._inputIndirectPercent_log);
			const financing = this.getNum(this._inputFinancingPercent_log);
			const profit = this.getNum(this._inputProfitPercent_log);

			if ((indirect + financing + profit) > 100) {
				Swal.fire({ icon: "warning", text: "La suma de porcentajes excede el 100%" });
				return false;
			}
			return true;
		}

		destroy() {
			this.listeners.forEach(({ element, eventType, handler }) => {
				element.removeEventListener(eventType, handler);
			});
			this.listeners = [];

			// NUEVO
			this.detachAutoNumeric();
		}

		isValidSpecificRules(): buttonSendCotiz_StatusValidationMessageDTO {
			let precioLista = round2(this.getNum(this._inputListPrice_log));
			let descuentoMaximo = round2(this.getNum(this._inputMaxDiscountPercent_log));
			let precioVenta = round2(this.getNum(this._inputMinSalePrice_log));


			if (!(precioLista > 0 && descuentoMaximo >= 0 && precioVenta > 0)) {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "Los valores numéricos deben ser mayores a 0, corrígelo. (el descuento puede ser 0)");
			}
			else {
				return new buttonSendCotiz_StatusValidationMessageDTO(true, "");
			}
		}
	}

	/**
	 * CASE C: Neodata: true, Tender: false
	 */
	class CaseC extends CostingForm {
		debounceTimeout: any;
		calculationTimeout: any; // 1. Nueva propiedad para el tiempo corto

		constructor() {
			super();
			this.typeCase = "C";
			this.title.textContent = "Cálculo Financiero";
			this.listeners = [];
			this.debounceTimeout = null;
			this.calculationTimeout = null; // 2. Inicializar
			this.applyCaseCRules();
			this.setInitialProposedAmount();
			this.initSequentialInputs();
			this.initPercentFromValues();
		}

		setInitialProposedAmount() {
			if (typeof globalVariables.totalAmount !== "undefined") {
				this.inputProposedAmountValue = globalVariables.totalAmount;
				this._inputProposedAmountValue.disabled = true;
			}
		}

		applyCaseCRules() {
			this.allInputs.forEach((input) => (input.disabled = true));
			this._inputDirectCost.disabled = false;
		}

		initSequentialInputs() {
			const sequentialOrder = [
				this._inputDirectCost,
				this._inputIndirectValue,
				this._inputFinancingValue,
				this._inputProfitValue,
				this._inputOperatingExpensesPercent,
				this._inputMaxDiscountPercent,
			];

			const inputHandler = (event) => {
				const currentIndex = sequentialOrder.indexOf(event.target);
				const nextInput = sequentialOrder[currentIndex + 1];

				// A. Limpiamos ambos timeouts anteriores para evitar colisiones
				clearTimeout(this.calculationTimeout);
				clearTimeout(this.debounceTimeout);

				// B. TIEMPO CORTO (200ms): Solo cálculos y actualizaciones visuales (Silencioso)
				this.calculationTimeout = setTimeout(() => {
					// Pasamos 'true' para indicar modo silencioso (sin alertas visuales aun)
					this.updateSalePrice(true);
					this.updatePercentFromValues();
				}, 200);

				// C. TIEMPO LARGO (2000ms): Validación estricta, Alertas y Reseteo
				this.debounceTimeout = setTimeout(() => {
					// Validación completa (modo silencioso = false)
					const isValidPartial = this.updateSalePrice(false);
					const isValidFinal = this.showFinalBalanceMessage();

					if (!isValidPartial || !isValidFinal) {
						// Resetear valor actual si no cumple las reglas
						const logInstance = AutoNumeric.getAutoNumericElement(event.target);
						if (logInstance) logInstance.clear();

						for (let i = currentIndex + 1; i < sequentialOrder.length; i++) {
							sequentialOrder[i].value = "";
							sequentialOrder[i].disabled = true;
						}
						return;
					}

					// Si todo está bien, activar siguiente casilla
					if (nextInput) nextInput.disabled = false;

				}, 2000);
			};

			sequentialOrder.forEach((input) => {
				if (input) {
					input.addEventListener("input", inputHandler);
					this.listeners.push({ element: input, eventType: "input", handler: inputHandler });
				}
			});

			const percentHandler = () => this.updateListPrice();
			this._inputOperatingExpensesPercent.addEventListener("input", percentHandler);
			this._inputMaxDiscountPercent.addEventListener("input", percentHandler);
		}

		// Modificamos este método para aceptar el modo silencioso
		updateSalePrice(silentMode = false) {
			const proposed = this.getNum(this._inputProposedAmountValue_log);
			const direct = this.getNum(this._inputDirectCost_log);
			const indirect = this.getNum(this._inputIndirectValue_log);
			const financing = this.getNum(this._inputFinancingValue_log);
			const profit = this.getNum(this._inputProfitValue_log);
			const opPercent = this.getNum(this._inputOperatingExpensesPercent_log);

			const subtotal = direct + indirect + financing + profit;

			// Evitar validación prematura si faltan datos
			if (subtotal === 0 && proposed > 0) return true;

			if (subtotal > proposed) {
				// Solo mostramos la alerta si NO estamos en modo silencioso (es decir, en el debounce de 2000ms)
				if (!silentMode) {
					Swal.fire({
						icon: "warning",
						text: "La suma de los costos no puede exceder el importe de la propuesta.",
					});
				}
				return false;
			}

			if (opPercent > 100) return false;

			const opValue = (proposed * opPercent) / 100;
			const minSalePrice = proposed + opValue;

			this.inputOperatingExpensesValue = opValue;
			this.inputMinSalePrice = minSalePrice;

			this.updateListPrice();
			return true;
		}

		showFinalBalanceMessage() {
			const direct = this.getNum(this._inputDirectCost_log);
			const indirect = this.getNum(this._inputIndirectValue_log);
			const financing = this.getNum(this._inputFinancingValue_log);
			const profit = this.getNum(this._inputProfitValue_log);
			const proposed = this.getNum(this._inputProposedAmountValue_log);

			// Si faltan datos no validamos aun
			if (!direct || !indirect || !financing || !profit) return true;

			const sumaComponentes = direct + indirect + financing + profit;
			const tolerance = 0.01;

			if (Math.abs(sumaComponentes - proposed) > tolerance) {
				// Este mensaje suele ser crítico, se deja igual, pero solo se llamará en el timeout de 2000ms
				Swal.fire({
					icon: "error",
					title: "Montos incorrectos",
					html: `Suma: ${sumaComponentes.toFixed(2)} vs Propuesta: ${proposed.toFixed(2)}`
				});
				return false;
			}
			return true;
		}

		updateListPrice() {
			const minSalePrice = this.getNum(this._inputMinSalePrice_log);
			const discountPercent = this.getNum(this._inputMaxDiscountPercent_log);

			const discountValue = (minSalePrice * discountPercent) / 100;
			const listPrice = minSalePrice / (1 - (discountPercent / 100));

			this.inputMaxDiscountValue = discountValue;
			this.inputListPrice = listPrice;
		}

		initPercentFromValues() {
			// En este caso, como ya llamamos a updatePercentFromValues en el timeout de 200ms,
			// los listeners directos aquí podrían ser redundantes dependiendo de tu flujo,
			// pero se dejan por seguridad si se editan campos no secuenciales.
			const valueInputs = [
				this._inputIndirectValue,
				this._inputFinancingValue,
				this._inputProfitValue
			];
			const handler = () => this.updatePercentFromValues();
			valueInputs.forEach((input) => {
				if (input) {
					input.addEventListener("input", handler);
					this.listeners.push({ element: input, eventType: "input", handler });
				}
			});
		}

		updatePercentFromValues() {
			const direct = this.getNum(this._inputDirectCost_log);
			const indirect = this.getNum(this._inputIndirectValue_log);
			const financing = this.getNum(this._inputFinancingValue_log);
			const profit = this.getNum(this._inputProfitValue_log);

			if (direct > 0) this.inputIndirectPercent = (indirect / direct) * 100;
			if (direct + indirect > 0) this.inputFinancingPercent = (financing / (direct + indirect)) * 100;
			if (direct + indirect + financing > 0) this.inputProfitPercent = (profit / (direct + indirect + financing)) * 100;
		}

		destroy() {
			this.listeners.forEach(({ element, eventType, handler }) => {
				element.removeEventListener(eventType, handler);
			});
			this.listeners = [];

			// Limpiamos ambos timeouts al destruir
			clearTimeout(this.debounceTimeout);
			clearTimeout(this.calculationTimeout);

			this.detachAutoNumeric();
		}

		isValidSum() {
			const directCost = round2(this.getNum(this._inputDirectCost_log)) || 0;
			const indirectCost = round2(this.getNum(this._inputIndirectValue_log)) || 0;
			const financingCost = round2(this.getNum(this._inputFinancingValue_log)) || 0;
			const profitCost = round2(this.getNum(this._inputProfitValue_log)) || 0;
			const operatingExpensesCost = round2(this.getNum(this._inputOperatingExpensesValue_log)) || 0;
			const precioVenta = round2(this.getNum(this._inputMinSalePrice_log)) || 0; // ✅ CORRECCIÓN: Declarado aquí

			if ((directCost + indirectCost + financingCost + profitCost + operatingExpensesCost) !== precioVenta) {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "La suma de los costos no coincide con el precio de venta");
			}
			else {
				return new buttonSendCotiz_StatusValidationMessageDTO(true, "")
			}
		}

		isValidSpecificRules(): buttonSendCotiz_StatusValidationMessageDTO {
			let precioLista = round2(this.getNum(this._inputListPrice_log));
			let descuentoMaximo = round2(this.getNum(this._inputMaxDiscountPercent_log));
			let precioVenta = round2(this.getNum(this._inputMinSalePrice_log));


			if (!(precioLista > 0 && descuentoMaximo >= 0 && precioVenta > 0)) {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "Los valores numéricos deben ser mayores a 0, corrígelo. (el descuento puede ser 0)");
			}
			else {
				return new buttonSendCotiz_StatusValidationMessageDTO(true, "");
			}
		}
	}


	const initCaseBySwitches = () => {
		// 1. Validar si existe un Total para operar
		// Si no hay total, bloqueamos la interfaz y salimos.
		if (typeof globalVariables.totalAmount === "undefined" || globalVariables.totalAmount <= 0) {
			//disableInterfaceNoTotal();
			activeCase = null;
			return;
		}

		// 2. Limpieza del caso activo anterior
		if (activeCase) {
			// Primero eliminamos los eventos para evitar que se disparen mientras limpiamos
			if (typeof activeCase.destroy === "function") {
				activeCase.destroy();
			}

			// Reseteamos los inputs (vaciar valores y ponerlos en disabled = true)
			// Esto asegura que el nuevo caso encuentre el formulario "limpio"
			if (typeof activeCase.resetInputs === "function") {
				activeCase.resetInputs();
			}
		}

		// 3. Reactivar elementos generales de la interfaz (Selects, Botones, etc.)
		enableGeneralInterface();

		// 4. Actualizar estado de los switches
		// Es vital leer el DOM actual antes de decidir qué clase instanciar
		isNeodata = switchNeodataOrigin.checked;
		isTender = switchTender.checked;

		// 5. Instanciar el nuevo caso según la lógica
		// El constructor de la nueva clase se encargará de:
		//    a) Habilitar solo los inputs necesarios (applyRules)
		//    b) Asignar el valor inicial basado en 'total'
		try {
			if (isNeodata && isTender) {
				activeCase = new CaseA();
			} else if (!isNeodata) {
				activeCase = new CaseB();
			} else if (isNeodata && !isTender) {
				activeCase = new CaseC();
			} else {
				console.warn("Combinación de switches no manejada.");
				activeCase = null;
			}
		} catch (error) {
			console.error("Error al inicializar el caso de costeo:", error);
		}
	};

	/**
	 * Función auxiliar para bloquear la interfaz cuando no hay Total.
	 * Se corrigió el selector CSS y la lógica de limpieza.
	 */
	const disableInterfaceNoTotal = () => {
		const selectors = [
			".costeo input",
			".costeo select",
			".costeo textarea",
			".adjuntos input[type='file']",
			".condiciones input",
			".condiciones textarea",
			".boton-final .boton-final-enviar", // Corregido selector (faltaba punto o espacio)
			".adjuntos .file-input-container"
		];

		document.querySelectorAll(selectors.join(", ")).forEach((el) => {
			// No deshabilitar si tiene la clase active (ej. pestañas)
			if (!el.classList.contains("active")) {
				if (el.type === "checkbox") {
					el.checked = false;
				} else {
					// Si es un input de AutoNumeric, lo ideal es limpiarlo via instancia, 
					// pero value = "" funciona como fallback visual.
					el.value = "";
				}
				el.disabled = true;
			} else {
				el.disabled = false;
			}
		});

		// Estilo gris para botones
		document.querySelectorAll(".btn-styles-cotizForm").forEach((btn) => {
			btn.style.backgroundColor = "#ccc";
			btn.style.color = "#666";
			btn.style.cursor = "not-allowed";
			btn.style.pointerEvents = "none";
			btn.style.transition = "none";
		});
	};

	/**
	 * Función auxiliar para habilitar la interfaz general antes de cargar el caso.
	 */
	const enableGeneralInterface = () => {
		const selectors = [
			".costeo .form-switch-Neodata",
			".adjuntos input[type='file']",
			".condiciones input",
			".condiciones textarea",
			".boton-final .boton-final-enviar",
			".adjuntos .file-input-container"
		];

		document.querySelectorAll(selectors.join(", ")).forEach((el) => {
			el.disabled = false;
			el.style.transition = "";
		});

		document.querySelectorAll(".btn-styles-cotizForm").forEach((btn) => {
			btn.style.backgroundColor = "";
			btn.style.color = "";
			btn.style.cursor = "pointer";
			btn.style.pointerEvents = "auto";
			btn.style.transition = "";
		});

		const selectUnidad = document.getElementById("unidadTiempo");
		if (selectUnidad) selectUnidad.style.backgroundColor = "white";
	};

	/**
	 * @description Lógica de inicialización principal que se ejecuta cuando el DOM está completamente cargado.
	 * Maneja la creación de la instancia de la clase de costeo correcta según los estados iniciales
	 * y los cambios en los switches.
	 */
	document.addEventListener("DOMContentLoaded", () => {

		initCaseBySwitches();

		switchNeodataOrigin.addEventListener("change", initCaseBySwitches);
		switchTender.addEventListener("change", initCaseBySwitches);
	});

	async function downloadFile() {
		const id = "1";
		try {
			const response = await fetch(`/api/ftp/download/${id}`);
			if (!response.ok) throw new Error("No se pudo descargar el archivo");

			// 1. Tomar el nombre desde el header Content-Disposition
			let filename = "archivo_descargado";
			const disposition = response.headers.get("Content-Disposition");
			if (disposition && disposition.includes("filename=")) {
				// Extraer el nombre después de filename=
				const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (match && match[1]) {
					filename = match[1].replace(/['"]/g, ""); // quitar comillas
				}
			}

			// 2. Obtener el blob
			const blob = await response.blob();

			// 3. Crear URL temporal para descarga
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename; // 👈 ahora usamos el nombre original
			document.body.appendChild(a);
			a.click();

			// 4. Limpiar
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error(error);
			alert("Error al descargar el archivo.");
		}
	}
	// JS
	function initTiempoEjecucionForm() {
		const form = document.getElementById("formTiempo");
		const inputTiempo = document.getElementById("tiempoEjecucion");
		const selectUnidad = document.getElementById("unidadTiempo");

		// Inicial: deshabilitado y forzamos el color
		setSelectState(true);

		function setSelectState(disabled) {
			selectUnidad.disabled = !!disabled;
			if (disabled) {
				selectUnidad.style.setProperty(
					"background-color",
					"#CCCCCC",
					"important"
				);
				selectUnidad.style.setProperty("color", "#666666", "important");
				selectUnidad.style.setProperty("cursor", "not-allowed", "important");
				selectUnidad.value = "";
			} else {
				selectUnidad.style.setProperty("background-color", "white", "important");
				selectUnidad.style.setProperty("color", "#000000", "important");
				selectUnidad.style.removeProperty("cursor");
			}
		}

		function evaluateAndSync() {
			const value = inputTiempo.value.trim();

			// Si alguien deshabilitó el input desde otro sitio, forzamos select gris
			if (inputTiempo.disabled) {
				setSelectState(true);
				return;
			}

			// Si hay valor > 0 activamos select, si no lo desactivamos
			if (value !== "" && parseFloat(value) > 0) {
				setSelectState(false);
			} else {
				setSelectState(true);
			}
		}

		// Impedir valores negativos o cero
		inputTiempo.addEventListener("input", () => {
			// Si el usuario escribe algo que no sea número, lo limpia
			const val = inputTiempo.value;

			// Bloquear signos negativos o ceros al inicio
			if (parseFloat(val) <= 0) {
				inputTiempo.value = ""; // lo limpia
			}

			evaluateAndSync();
		});

		// Observador por si algún otro script cambia el atributo disabled del input
		const mo = new MutationObserver((muts) => {
			for (const m of muts) {
				if (m.type === "attributes" && m.attributeName === "disabled") {
					evaluateAndSync();
				}
			}
		});
		mo.observe(inputTiempo, { attributes: true, attributeFilter: ["disabled"] });



		// Ejecutar una vez al inicio
		evaluateAndSync();

	}

	initTiempoEjecucionForm();

	// La función `initTaskSearch` es la que inicia toda la lógica de búsqueda.
	function initTaskSearch() {

		const opnumberInput = document.getElementById("opnumber");
		const opbtn = document.getElementById("opbtn");

		opbtn.addEventListener("pointerdown", async () => {
			const taskId = opnumberInput.value.trim();

			if (!taskId) {
				Swal.fire({
					icon: "warning",
					title: "Entrada requerida",
					text: "Por favor, ingrese un número de tarea.",
					confirmButtonText: "Aceptar",
				});
				return;
			}

			// Llamar a la función del backend para obtener la tarea
			const { task, message } = await fetchQuotationTaskById(taskId);

			if (task) {
				Swal.fire({
					icon: "success",
					html: message,
					confirmButtonText: "Aceptar",
				});
				populateOpportunityForm(task);
			} else {
				Swal.fire({
					icon: "error",
					text: message,
					confirmButtonText: "Aceptar",
				});
			}
		});
	}

	function populateOpportunityForm(taskData) {
		// Si los datos de la oportunidad existen en el objeto de la tarea
		if (taskData && taskData.crm_data) {
			const crmData = taskData.crm_data;

			// Asignar datos de la oportunidad a los inputs
			document.getElementById("CRM_OpportunityNumber").value =
				crmData.CRM_OpportunityNumber || "";
			document.getElementById("CRM_ContactName").value =
				crmData.CRM_ContactName || "";
			document.getElementById("CRM_ContactType").value =
				crmData.CRM_ContactType || "";
			document.getElementById("CRM_AssignedSalesperson").value =
				crmData.CRM_AssignedSalesperson || "";
			document.getElementById("CRM_ContactAdress").value =
				crmData.CRM_ContactAdress || "";
			document.getElementById("CRM_ContactColonia").value =
				crmData.CRM_ContactColonia || "";
			document.getElementById("CRM_ContactCity").value =
				crmData.CRM_ContactCity || "";
			document.getElementById("CRM_ContactNumber").value =
				crmData.CRM_ContactNumber || "";
			document.getElementById("CRM_ContactCountry").value =
				crmData.CRM_ContactCountry || "";
			document.getElementById("CRM_ContactLegalIdentifier").value =
				crmData.CRM_ContactLegalIdentifier || "";
			document.getElementById("CRM_ContactZip").value =
				crmData.CRM_ContactZip || "";
			document.getElementById("CRM_ContactState").value =
				crmData.CRM_ContactState || "";
			document.getElementById("CRM_ContactEmail").value =
				crmData.CRM_ContactEmail || "";
		}
	}



	// Esta es la función que realiza la llamada a la API
	async function fetchQuotationTaskById(taskId) {
		const url = `/api/quotation/cotiz/tasks/${taskId}`;

		try {
			const response = await axios.get(url, {
				headers: {
					Accept: "application/json",
				},
			});

			// El API debe devolver un único objeto, no un array
			const task = response.data;
			task_SellerUserID = task.SellerUserID
			task_CRM_OpportunityID = task.CRM_OpportunityID
			task_FormID = task.FormID
			task_TaskID = task.TaskID
			task_Status = task.Status

			// Si la respuesta es exitosa pero el objeto está vacío, se retorna null
			if (!task || Object.keys(task).length === 0) {
				return {
					message: "No se encontró la tarea",
					task: null
				}
			}

			// Verifica que esta atendido
			if (task_Status === 'COMPLETADO') {
				return {
					message: "La tarea está completada",
					task: null
				}
			}
			else {
				return {
					message: "Tarea encontrada con éxito",
					task: task
				}
			}

		} catch (error) {
			if (
				axios.isAxiosError(error) &&
				error.response &&
				error.response.status === 404
			) {
			} else {
				console.error(
					"Error al obtener la tarea:",
					error.response ? error.response.data : error.message
				);
			}
			return {
				message: "No se encontro la tarea",
				task: null
			}
		}
	}

	// cuando la página se cargue por completo.
	initTaskSearch();





	// DTO para el estado de validación del botón de enviar cotización
	class buttonSendCotiz_StatusValidationMessageDTO {
		isValid: boolean;
		message: string;
		constructor(valid: boolean, message: string) {
			this.isValid = valid;
			this.message = message;
		}
	}


	// Lógica para el botón de enviar cotización
	class buttonSendCotiz {

		is_valid_Forms_Task() {
			if (document.getElementById("CRM_OpportunityNumber").value !== "") {
				return new buttonSendCotiz_StatusValidationMessageDTO(true, "OK");
			}
			return new buttonSendCotiz_StatusValidationMessageDTO(false, "Busca primero un # de tarea");
		}


		is_valid_Forms_ConceptsCatalog() {
			const local = new LocalDb(); // ✅ CORRECCIÓN 1: Agregado 'const'

			// Verificar si LocalDb tiene el método isEmpty
			if (typeof local.isEmpty === 'function') {
				if (local.isEmpty()) {
					return new buttonSendCotiz_StatusValidationMessageDTO(false, "El catálogo de conceptos está vacío, por favor agrega conceptos");
				}
			} else {
				// Alternativa: verificar si tiene datos usando otros métodos
				const datos = local.getAll ? local.getAll() : local.data;
				if (!datos || (Array.isArray(datos) && datos.length === 0) || Object.keys(datos || {}).length === 0) {
					return new buttonSendCotiz_StatusValidationMessageDTO(false, "El catálogo de conceptos está vacío, por favor agrega conceptos");
				}
			}

			return new buttonSendCotiz_StatusValidationMessageDTO(true, "OK");
		}


		is_valid_Forms_FinancialCalculations() {
			if (activeCase === null) {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "Realiza primero el cálculo financiero");
			}

			if (activeCase) {

				//valiacion de suma de costos
				if (activeCase.typeCase === 'C' || activeCase.typeCase === 'A') {
					// Validacion suma correcta
					let result = activeCase.isValidSum();
					if (result.isValid === false) {
						return new buttonSendCotiz_StatusValidationMessageDTO(false, "La suma de los costos no coincide con el precio de venta");
					}
				}

				if (activeCase.typeCase === 'A') {
					let result = activeCase.isValidSalePrice();
					if (result.isValid === false) {
						return new buttonSendCotiz_StatusValidationMessageDTO(false, result.message);
					}

				} else if (activeCase.typeCase === 'B' || activeCase.typeCase === 'C') {
					let result = activeCase.isValidSpecificRules()

					if (result.isValid === false) {
						return new buttonSendCotiz_StatusValidationMessageDTO(false, result.message);
					}
				}
			}

			// ✅ CORRECCIÓN: Agregado return cuando todo es válido
			return new buttonSendCotiz_StatusValidationMessageDTO(true, "OK");
		}

		is_valid_TimeExecution() {
			const tiempoEjecucion = document.getElementById("tiempoEjecucion"); // ✅ CORRECCIÓN 2: Agregado espacio y 'const'
			const unidadTiempo = document.getElementById("unidadTiempo");

			// Validar que ambos campos tengan valor
			if (tiempoEjecucion.value === "" || unidadTiempo.value === "") {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "Coloca el tiempo de entrega y su unidad");
			}



			if (tiempoEjecucion.value === "" || Number(tiempoEjecucion.value) <= 0) {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "Coloca el tiempo de entrega y su unidad");
			}
			return new buttonSendCotiz_StatusValidationMessageDTO(true, "OK");
		}

		is_valid_Tax_CurrencyType() {
			const taxCurrencyType = document.getElementById("Finance_CurrencyType");
			const Finance_TaxCurrencyType = document.getElementById("Finance_TaxCurrencyType");
			if (Finance_TaxCurrencyType.value === "") {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "Coloca el tipo de moneda de la cotización");
			}
			if (taxCurrencyType.value === "") {
				return new buttonSendCotiz_StatusValidationMessageDTO(false, "Coloca el tipo de moneda de los impuestos");
			}
			return new buttonSendCotiz_StatusValidationMessageDTO(true, "OK");
		}


		is_valid_CotizCreatedIng() {
			const validations = [ // ✅ CORRECCIÓN 3: Agregado 'const'
				this.is_valid_Forms_Task(),
				this.is_valid_Forms_ConceptsCatalog(),
				this.is_valid_Forms_FinancialCalculations(),
				this.is_valid_Tax_CurrencyType(),
				this.is_valid_TimeExecution()
			];

			for (let i = 0; i < validations.length; i++) {
				if (validations[i].isValid === false) {
					Swal.fire({
						icon: "warning",
						title: "Validación fallida",
						text: validations[i].message
					});
					return false;
				}
			}
			return true;
		}

	}


	class TerminosYCondiciones {
		toJson() {
			const adress = document.getElementById("address")?.value;
			return adress
		}
	}

	class TiempoDeEjecucion {
		/**
		 * Convierte los valores del formulario en un objeto JSON serializable.
		 * @returns {{ RunTimeNumber: string, RunTimeType: string }}
		 */
		toJson() {
			const tiempoEjecucion = document.getElementById("tiempoEjecucion")?.value || "";
			const unidadTiempo = document.getElementById("unidadTiempo")?.value || "";

			return {
				RunTimeNumber: tiempoEjecucion,
				RunTimeType: unidadTiempo
			};
		}
	}


	function datosOportunidadToJson() {
		// Helper para asegurar que siempre devuelva string limpio
		const toStr = (id) => {
			const el = document.getElementById(id);
			return el && el.value !== undefined ? String(el.value).trim() : "";
		};

		return {
			CRM_OpportunityNumber: toStr("CRM_OpportunityNumber"),
			CRM_ContactName: toStr("CRM_ContactName"),
			CRM_ContactType: toStr("CRM_ContactType"),
			CRM_AssignedSalesperson: toStr("CRM_AssignedSalesperson"),
			CRM_ContactAdress: toStr("CRM_ContactAdress"),
			CRM_ContactColonia: toStr("CRM_ContactColonia"),
			CRM_ContactCity: toStr("CRM_ContactCity"),
			CRM_ContactNumber: toStr("CRM_ContactNumber"),
			CRM_ContactCountry: toStr("CRM_ContactCountry"),
			CRM_ContactLegalIdentifier: toStr("CRM_ContactLegalIdentifier"),
			CRM_ContactZip: toStr("CRM_ContactZip"),
			CRM_ContactState: toStr("CRM_ContactState"),
			CRM_ContactEmail: toStr("CRM_ContactEmail"),
		};
	}

	async function getUserID() {
		try {
			const res = await fetch("/api/Operaciones/Ingenieria/Cotiz/CotizCreatedIng/user_id", {
				credentials: "include" // 🔹 Importante para que Flask lea la sesión
			});
			const data = await res.json();

			if (res.ok) {
				console.log("✅ UserID:", data.user_id);
				return data.user_id;
			} else {
				console.warn("⚠️ Error:", data.error);
				return null;
			}
		} catch (err) {
			console.error("❌ Error al obtener el UserID:", err);
			return null;
		}
	}






	async function cotizCreatedIng_ToJson(docs_id) {
		let localDb = new LocalDb();

		const htmlData = {
			opportunity: datosOportunidadToJson(),
			items: localDb.getAll(),
			financialCalculation: activeCase!.toJson(),
			termsAndConditions: new TerminosYCondiciones().toJson(),
			executionTime: new TiempoDeEjecucion().toJson()
		}

		const res = await fetch("/api/Operaciones/Ingenieria/Cotiz/CotizCreatedIng/user_id", {
			credentials: "include",
		});
		const data = await res.json();

		if (!res.ok) throw new Error(data?.error || "Respuesta no OK");

		Q_CostingHead_data = JSON.stringify({
			// 🔹 Automático: generado como CONCAT(CostingNum, '-', Version)
			// "CostingID": "1000-1",

			// 🔹 Automático: valor por defecto del sistema (fecha actual)
			// "CostingDate": "2025-10-14T10:00:00",

			// 🔹 Automático: IDENTITY(1000,1)
			// "CostingNum": 1000,
			"TaskID": task_TaskID,

			"Version": 1,
			// "QuotationTypeID": sub1.QuotationTypeID,
			"UserID": data.user_id,
			// "RoleID": sub2.RoleID,
			"SellerUserID": task_SellerUserID,
			// "CompanyID": sub2.CompanyID,
			// "DivisionID": sub2.DivisionID,
			// "DepartamentID": sub2.DepartamentID,

			"CaseCost": activeCase.typeCase,

			// 🔹 Datos de financialCalculation (tomados desde htmlData)
			"DirectCost": htmlData.financialCalculation.directCost,
			"IndirectAmount": htmlData.financialCalculation.indirect.amount,
			"IndirectPercent": htmlData.financialCalculation.indirect.percent,
			"FinanceAmount": htmlData.financialCalculation.financing.amount,
			"FinancePercent": htmlData.financialCalculation.financing.percent,
			"UtilityAmount": htmlData.financialCalculation.profit.amount,
			"UtilityPercent": htmlData.financialCalculation.profit.percent,
			"OperationAmount": htmlData.financialCalculation.operatingExpenses.amount,
			"OperationPercent": htmlData.financialCalculation.operatingExpenses.percent,
			"SalePriceMin": htmlData.financialCalculation.minSalePrice,
			"DiscountMaxAmount": htmlData.financialCalculation.maxDiscount.amount,
			"DiscountMaxPercent": htmlData.financialCalculation.maxDiscount.percent,
			"SalePriceList": htmlData.financialCalculation.listPrice,
			"OvercostFactor": 0,//htmlData.financialCalculation.proposalAmount ?? 0,

			"Active": true,

			// 🔹 CRM y control de proceso
			"CRM_OpportunityID": task_CRM_OpportunityID,
			"FormID": task_FormID,
			"ApprovalProcessID": null,
			"PreviusDocsID": null,
			"DocsID": docs_id,

			// 🔹 Bloques Terms y Execution Time desde htmlData
			"TechnicalTermsAndConditions": htmlData.termsAndConditions,
			"RunTimeNumber": htmlData.executionTime.RunTimeNumber,
			"RunTimeType": htmlData.executionTime.RunTimeType,
			"CurrencyCode": htmlData.financialCalculation.CurrencyCode,
			"TaxCode": htmlData.financialCalculation.TaxCode,
			"Q_CostingDetail": localDb.to_Json_Q_CostingDetail(1)
		});


		return await enviarCostingCompleto(Q_CostingHead_data);
	}


	// 🔹 Función para enviar al backend
	async function enviarCostingCompleto(data) {
		try {
			const response = await fetch('/api/Operaciones/Ingenieria/Cotiz/CotizCreatedIng/Q_CostingHead/Completo', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: data
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || 'Error al crear costing');
			}

			return await response.json();

		} catch (error) {
			console.error("Error al enviar costing:", error);
			alert("Error al crear la cotización: " + error.message);
			throw error;
		}
	}


	/**
	 * Envía los archivos seleccionados al backend Flask para CotizCreatedIng.
	 * Usa el endpoint: /api/Operaciones/Ingenieria/Cotiz/CotizCreatedIng/uploadFile
	 */
	/**
	* Envía la cotización y, si existen, los archivos seleccionados al backend.
	* 1. Crea la entrada de cotización (Q_CostingHead).
	* 2. Si hay archivos, los sube.
	* 3. Si la subida es exitosa, actualiza la cotización con el DocsID.
	*/

	async function enviarArchivosCotizCreatedIng() {
		if (validador.is_valid_CotizCreatedIng()) {
			try {
				// 🔹 1. Obtener archivos (no falla si está vacío)
				const uploader = document.getElementById('uploader');
				const archivos = (globalVariables?.getFiles && globalVariables.getFiles()) || uploader?.files || [];

				// 🔹 2. Mostrar mensaje de carga genérico con advertencia
				Swal.fire({
					title: 'Guardando cotización',
					html: `
					<p>Por favor, espere...</p>
					<div style="margin-top: 15px; padding: 10px; background-color: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px;">
						<strong style="color: #856404;">⚠️ Importante:</strong>
						<p style="color: #856404; margin: 5px 0 0 0; font-size: 0.9em;">
							No cierre ni actualice esta ventana mientras el proceso esté en curso.
						</p>
					</div>
				`,
					allowOutsideClick: false,
					showConfirmButton: false,

					didOpen: () => Swal.showLoading(),
				});

				// 🔹 3. Crear la cotización (Q_CostingHead) PRIMERO
				let docsID_tmp = null;


				let responseData = await cotizCreatedIng_ToJson(docsID_tmp);
				let TaskID = task_TaskID;
				const newCostingID = responseData.data.CostingID;
				const newCostingNum = responseData.data.CostingNum;


				if (!newCostingID) {
					throw new Error("No se pudo obtener el CostingID después de crear la cotización.");
				}

				// 🔹 4. Enviar correo automáticamente
				try {
					const emailResponse = await fetch(`/api/Operaciones/Ingenieria/Cotiz/CotizCreatedIng/get_data_for_email?task_id=${TaskID}`, {
						method: 'GET',
						credentials: 'include'
					});
					const emailResult = await emailResponse.json();

					if (emailResponse.ok && emailResult.success) {
						console.log("📧 Correo enviado correctamente");
					} else {
						console.warn("⚠️ El correo no se envió:", emailResult.mensaje || emailResult);
					}
				} catch (emailError) {
					console.error("💥 Error al enviar correo:", emailError);
				}

				// 🔹 5. Verificar si hay archivos para subir
				if (archivos && archivos.length > 0) {
					Swal.update({
						html: `
						<p>Cotización creada (ID: ${newCostingID}).</p>
						<p>Subiendo <strong>${archivos.length}</strong> archivo(s)...</p>
						<div style="margin-top: 15px; padding: 10px; background-color: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px;">
							<strong style="color: #856404;">⚠️ Importante:</strong>
							<p style="color: #856404; margin: 5px 0 0 0; font-size: 0.9em;">
								No cierre ni actualice esta ventana mientras el proceso esté en curso.
							</p>
						</div>
					`
					});

					const config = configurarTimeoutDinamico(archivos.length);
					const controller = new AbortController();
					const timeout = setTimeout(() => controller.abort(), config.timeoutMs);

					const formData = new FormData();
					for (const file of archivos) {
						formData.append('archivos[]', file);
					}
					formData.append('categoria', 'CotizCreatedIng');
					formData.append("CostingID", newCostingID);

					const response = await fetch(
						'/api/Operaciones/Ingenieria/Cotiz/CotizCreatedIng/uploadFile',
						{
							method: 'POST',
							body: formData,
							// signal: controller.signal,
						}
					);

					const result = await response.json();
					clearTimeout(timeout);

					if (response.ok && result.success) {
						const updateResponse = await fetch('/api/Operaciones/Ingenieria/Cotiz/CotizCreatedIng/updateCostingHead-docsID', {
							method: 'POST',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({
								"CostingID": newCostingID,
								"DocsID": result.docs_id
							})
						});

						if (!updateResponse.ok) {
							const updateError = await updateResponse.json();
							throw new Error(updateError.error || "Error al actualizar el DocsID en la cotización.");
						}

						await Swal.fire({
							icon: 'success',
							title: '✅ Cotización y archivos guardados',
							html: `
							<p># Costeo <strong>${newCostingNum}</strong></p>
							<p>Archivos subidos: <strong>${result.archivos_subidos}</strong></p>
						`,
							confirmButtonText: 'Continuar',
						});

						// Recargar la página para limpiar el caché
						window.location.reload();

					} else {
						console.error('❌ Error al subir:', result);
						throw new Error(result.message || 'Error al subir los archivos.');
					}

				} else {
					// 🔹 6. No hay archivos que subir, mostrar mensaje simple
					await Swal.fire({
						icon: 'success',
						title: '✅ Cotización guardada',
						html: `
						<p>Cotización ID: <strong>${newCostingNum}</strong></p>
						<p>(No se adjuntaron archivos)</p>
					`,
						confirmButtonText: 'Continuar',
					});

					// Recargar la página para limpiar el caché
					window.location.reload(true);
				}

			} catch (error) {
				// 🔹 7. Manejo de errores (general)
				console.error('💥 Error en enviarArchivosCotizCreatedIng:', error);
				Swal.close();

				if (error.name === 'AbortError') {
					await Swal.fire({
						icon: 'error',
						title: '⏰ Tiempo excedido',
						text: 'La carga tardó demasiado y fue cancelada automáticamente.',
						confirmButtonText: 'Entendido',
					});
				} else {
					await Swal.fire({
						icon: 'error',
						title: '💥 Error al guardar',
						text: error.message || 'Ocurrió un error inesperado. Revisa la consola.',
						confirmButtonText: 'Entendido',
					});
				}
			}
		}
	}


	async function enviarEmailPorTask(taskId) {
		try {
			const res = await fetch(`/api/Operaciones/Ingenieria/Cotiz/CotizCreatedIng/get_data_for_email?task_id=${taskId}`, {
				method: 'GET',
				credentials: 'include'  // 🔐 importante si necesitas sesión
			});

			const data = await res.json();

			if (res.ok) {
			} else {
				console.warn("⚠️ Error al enviar correo:", data);
			}
		} catch (err) {
			console.error("❌ Error de red o servidor:", err);
		}
	}


	/**
	 * Calcula el timeout y tiempo estimado dinámicamente según número de archivos.
	 */
	function configurarTimeoutDinamico(numArchivos) {
		// 🔹 Cálculo basado en la lógica revisada: (n * 40s) + 30s base
		let tiempoEstimadoSegundos = (numArchivos * 40) + 30;

		// 🔹 Convertir a milisegundos
		let timeoutMs = tiempoEstimadoSegundos * 1000;

		// 🔹 Limitar entre 60s y 300s (1 a 5 minutos)
		timeoutMs = Math.min(Math.max(timeoutMs, 60_000), 300_000);

		// 🔹 Calcular minutos redondeados hacia arriba
		const estimadoMinutos = Math.ceil(timeoutMs / 60000);

		return { timeoutMs, estimadoMinutos };
	}



	function esp() {
		const label = document.querySelector(".costeo #ApprovalAccessDistributorsTable_length label");
		if (label) {
			label.childNodes[0].textContent = "Mostrar ";
			label.childNodes[label.childNodes.length - 1].textContent = " registros";
		}
	}




	function traducirInfo() {
		const el = document.getElementById("ApprovalAccessDistributorsTable_info");
		if (!el) return;

		const txt = el.textContent.trim();

		// Detecta el formato estándar
		const match = txt.match(/Showing\s+(\d+)\s+to\s+(\d+)\s+of\s+(\d+)/i);
		if (!match) return;

		const [_, start, end, total] = match;

		el.textContent = `Mostrando ${start} a ${end} de ${total} registros`;
	}


}